import { useTranslation } from 'react-i18next';
import { formatGetal } from '../format';
import { weeknummer } from '../week';
import type { RichtingVelden, Weekcijfer } from '../work-quality.api';

export interface RichtingstekstProps extends RichtingVelden {
  /** Het hoofdcijfer van de week ervoor; met een percentage komt "dan week 42 (31%)" erachter. */
  vorige?: Weekcijfer;
}

const OMHOOG = '▲';
const OMLAAG = '▼';

/**
 * De richting tegenover vorige week, zoals de dienst hem vergeleek (M6, EXPERIENCE.md Richtingstekst).
 *
 * "▲ 7 punten hoger dan week 42 (31%)", "▼ 1 punt lager", "gelijk aan week 42 (31%)" of "geen vergelijking".
 * De pijl is `aria-hidden`; het woord draagt de betekenis. Groen voor hoger, amber voor lager, gedempt voor
 * gelijk en geen vergelijking; nooit rood. Herbruikbaar op Team (story 10).
 */
export function Richtingstekst({ richting, verschilPunten, vorige }: RichtingstekstProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const metVorige = typeof vorige?.percentage === 'number' ? { week: weeknummer(vorige.weekKey), percentage: formatGetal(vorige.percentage, locale) } : null;
  const punten = typeof verschilPunten === 'number' ? verschilPunten : null;

  let kleur = 'text-[var(--foreground-muted)]';
  let teken: string | null = null;
  let tekst = t('qa.rapportkaart.richtingGeenVergelijking');
  if ((richting === 'hoger' || richting === 'lager') && punten !== null) {
    const hoger = richting === 'hoger';
    kleur = hoger ? 'text-[var(--success-text)]' : 'text-[var(--warning-text)]';
    teken = hoger ? OMHOOG : OMLAAG;
    const opties = { count: punten, punten: formatGetal(punten, locale) };
    tekst = metVorige
      ? t(hoger ? 'qa.rapportkaart.richtingHogerDan' : 'qa.rapportkaart.richtingLagerDan', { ...opties, ...metVorige })
      : t(hoger ? 'qa.rapportkaart.richtingHoger' : 'qa.rapportkaart.richtingLager', opties);
  } else if (richting === 'gelijk') {
    tekst = metVorige ? t('qa.rapportkaart.richtingGelijkAan', metVorige) : t('qa.rapportkaart.richtingGelijk');
  }

  return (
    <span className={`inline-flex items-baseline gap-1 text-[0.75rem] font-medium ${kleur}`} data-testid="werkkwaliteit-richting">
      {teken !== null && <span aria-hidden="true">{teken}</span>}
      {teken !== null && ' '}
      <span>{tekst}</span>
    </span>
  );
}
